import React, { useCallback, useState } from 'react';

import { replaceCurly } from '@eika-infrastruktur/format';
import { useReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';
import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';
import { Modal } from '@eika/modal';

import piwik from '@/piwik/piwik';

import ApplePayModalContent from '@domain/digital-wallet/components/apple-pay/ApplePayModalContent';
import { generateAddedToWalletText } from '@domain/digital-wallet/components/apple-pay/generateApplePayStatusTexts';
import {
    getApplePayCardStatusKb,
    getApplePayCardStatusTe,
} from '@domain/digital-wallet/hooks/use-apple-wallet-card-token-status';
import useHandleApplePayProvisioningEvent from '@domain/digital-wallet/hooks/use-handle-apple-pay-provisioning-event';
import { useOnboardToAppleWallet } from '@domain/digital-wallet/hooks/use-onboard-to-apple-wallet';
import {
    ActivationCardToken,
    ApplePayEligibleCard,
    Card,
    CreditCard,
    CreditCardTokenStatus,
    DeviceType,
    WalletCreditCard,
} from '@generated-types';
import useText from '@hooks/use-text';

import useInvalidateApplePay from '../../hooks/query/use-invalidate-apple-pay';
import { AppleWalletService } from '../../services';
import { PendingCardTokensList } from '../../types';
import ApplePayPendingCardToken from './ApplePayPendingCardToken';

import addToAppleWallet from './AddToAppleWallet.svg';
import './ApplePay.scss';
import applePay from './ApplePay.svg';

export type ApplePayTeCardData = {
    source: 'te';
    card: Card;
    eligible: ApplePayEligibleCard;
    pendingTokens: ActivationCardToken[];
};

export type ApplePayKbCardData = {
    source: 'kb';
    card: CreditCard;
    eligibleCard: WalletCreditCard;
};

type ApplePayCardData = ApplePayTeCardData | ApplePayKbCardData;

type ApplePayContentProps = {
    data: ApplePayCardData;
};

export const ApplePayContent = ({ data }: ApplePayContentProps) => {
    const text = useText();
    const nativeProps = useReactNativeProps();

    const invalidateTokens = useInvalidateApplePay();
    const onboardCard = useOnboardToAppleWallet();
    const { status: eventStatus, cleanup } = useHandleApplePayProvisioningEvent();

    const { canOnboard, hasActiveCardTokens, hasPendingTokens, canOnboardWatch, canOnboardPhone } =
        data.source === 'te' ? getApplePayCardStatusTe(data, nativeProps) : getApplePayCardStatusKb(data, nativeProps);

    const pendingCardTokens: PendingCardTokensList =
        data.source === 'te'
            ? AppleWalletService.pairTePendingTokens(
                  [data.card],
                  data.pendingTokens,
                  nativeProps.appleWallet?.pendingCardTokens || [],
              )
            : AppleWalletService.pairKbPendingTokens(
                  [data.card],
                  [data.eligibleCard],
                  nativeProps.appleWallet?.pendingCardTokens || [],
              );

    const [open, setOpen] = useState(false);

    const cleanupAndInvalidate = useCallback(async () => {
        await invalidateTokens();
        cleanup();
    }, [cleanup, invalidateTokens]);

    const startOnboarding = useCallback(() => {
        setOpen(true);

        const payload =
            data.source === 'te'
                ? AppleWalletService.toProvisioningPayload(data)
                : AppleWalletService.pairToProvisioningPayload(data);
        onboardCard(payload);
    }, [data, onboardCard]);

    const cleanupAndRetryOnboarding = useCallback(() => {
        cleanup();
        const payload =
            data.source === 'te'
                ? AppleWalletService.toProvisioningPayload(data)
                : AppleWalletService.pairToProvisioningPayload(data);
        onboardCard(payload);
    }, [cleanup, data, onboardCard]);

    const addedDevices =
        data.source === 'te'
            ? data.eligible.tokens.filter((token) => token.status === 'ACTIVE').map((token) => token.deviceType)
            : (data.eligibleCard.tokens
                  .filter((token) => token.status === CreditCardTokenStatus.ACTIVE)
                  .map((token) => token.device.type)
                  .filter((type) => !!type) as string[]);

    const awaitingDevices = pendingCardTokens.map((item) =>
        item.source === 'te' ? item.token.deviceType : (item.token.device.type as DeviceType),
    );

    return (
        <div className="apple-pay" data-testid="apple-pay-wrapper">
            {(hasActiveCardTokens || hasPendingTokens) && (
                <div className="apple-pay__active-info">
                    <img src={applePay} alt={text.applePay} />
                    <span data-testid="apple-pay-active-info-text">
                        {generateAddedToWalletText(text, addedDevices, awaitingDevices)}
                    </span>
                </div>
            )}

            <ApplePayPendingCardToken pendingCardTokens={pendingCardTokens} />

            {canOnboard && (
                <button
                    data-testid="apple-pay-onboarding-button"
                    data-track-id={piwik.applePay.addToAppleWallet.dataTrackId}
                    data-track-linkname={piwik.applePay.addToAppleWallet.dataTrackLinkname}
                    className="apple-pay-button"
                    type="button"
                    onClick={startOnboarding}
                >
                    <img src={addToAppleWallet} alt="" />
                    <span className="screen-reader-text">{text.addToAppleWallet}</span>
                </button>
            )}

            {canOnboard && (hasActiveCardTokens || hasPendingTokens) && (canOnboardWatch || canOnboardPhone) && (
                <AttentionMessage
                    data-testid="eligible-card-attention-message"
                    type="default"
                    id={
                        canOnboardWatch
                            ? 'eligible-card-attention-message-watch'
                            : 'eligible-card-attention-message-iphone'
                    }
                    text={
                        <span className="semibold" data-testid="eligible-card-attention-message-text">
                            {replaceCurly(text.youCanAlsoAddYourCardTo, canOnboardWatch ? text.watch : text.iPhone)}
                        </span>
                    }
                    clickableItem={text.toDoThisClickTheLinkAbove}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}

            <Modal open={open} setOpen={setOpen} onClose={cleanupAndInvalidate} contentShouldStretch>
                <ApplePayModalContent
                    retry={cleanupAndRetryOnboarding}
                    isSuccess={eventStatus === 'success'}
                    isError={eventStatus === 'error'}
                    done={() => setOpen(false)}
                >
                    <div
                        className="apple-pay-modal__pending-state-wrapper"
                        data-testid="apple-pay-onboarding-pending-state"
                    >
                        <Loader />
                    </div>
                </ApplePayModalContent>
            </Modal>
        </div>
    );
};
